import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAccessibility } from './AccessibilityProvider';
import { Settings, Type, Contrast, Volume2, X, Accessibility, SkipForward } from 'lucide-react';

const AccessibilityControls = () => {
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef(null);
  const buttonRef = useRef(null);
  const { 
    fontSize, 
    setFontSize, 
    highContrast, 
    setHighContrast, 
    announce 
  } = useAccessibility();

  const handleFontSizeChange = (newSize) => {
    setFontSize(newSize);
    announce(`גודל הטקסט שונה ל${newSize === 'small' ? 'קטן' : newSize === 'large' ? 'גדול' : newSize === 'xlarge' ? 'גדול מאוד' : 'רגיל'}`);
  };

  const handleContrastToggle = () => {
    setHighContrast(!highContrast);
    announce(highContrast ? 'ניגודיות רגילה' : 'ניגודיות גבוהה');
  };

  const toggleControls = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      announce('פקדי נגישות נפתחו');
    } else {
      announce('פקדי נגישות נסגרו');
    }
  };

  const closeControls = useCallback(() => {
    setIsOpen(false);
    announce('פקדי נגישות נסגרו');
    buttonRef.current?.focus();
  }, [announce]);

  // Handle click outside to close and focus management
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && panelRef.current && !panelRef.current.contains(event.target) && !buttonRef.current?.contains(event.target)) {
        closeControls();
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        closeControls();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      
      // Focus the first focusable element in the panel
      setTimeout(() => {
        const firstButton = panelRef.current?.querySelector('button');
        if (firstButton) {
          firstButton.focus();
        }
      }, 100);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, closeControls]);

  return (
    <>
      {/* Floating Icon Button */}
      <button
        ref={buttonRef}
        onClick={toggleControls}
        className="fixed top-1/2 right-4 transform -translate-y-1/2 z-50 bg-orange-500 hover:bg-orange-600 text-white p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
        aria-label={isOpen ? 'סגור פקדי נגישות' : 'פתח פקדי נגישות'}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        <Accessibility className="h-6 w-6" />
      </button>

      {/* Controls Panel */}
      {isOpen && (
        <div 
          ref={panelRef}
          className="accessibility-controls fixed top-1/2 right-16 transform -translate-y-1/2 z-50 bg-white shadow-xl rounded-lg p-6 border border-gray-200 w-80 max-w-sm max-h-96 overflow-y-auto animate-in slide-in-from-right-2 duration-200"
          role="dialog"
          aria-labelledby="accessibility-title"
          aria-modal="true"
          style={{
            maxWidth: 'calc(100vw - 2rem)',
            right: '4rem',
            left: 'auto'
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <h3 id="accessibility-title" className="text-lg font-semibold text-gray-800 flex items-center">
              <Settings className="h-5 w-5 text-gray-600 ml-2" />
              הגדרות נגישות
            </h3>
            <button
              onClick={closeControls}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 rounded"
              aria-label="סגור פקדי נגישות"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          <div className="space-y-4">
            {/* Font Size Controls */}
            <fieldset>
              <legend className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <Type className="h-4 w-4 ml-1" />
                גודל טקסט
              </legend>
              <div className="flex space-x-2 space-x-reverse">
                {[
                  { value: 'small', label: 'קטן' },
                  { value: 'normal', label: 'רגיל' },
                  { value: 'large', label: 'גדול' },
                  { value: 'xlarge', label: 'גדול מאוד' }
                ].map(({ value, label }) => (
                  <button
                    key={value}
                    onClick={() => handleFontSizeChange(value)}
                    className={`px-3 py-1 text-xs rounded transition-colors ${
                      fontSize === value
                        ? 'bg-orange-500 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                    aria-pressed={fontSize === value}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </fieldset>

            {/* High Contrast Toggle */}
            <fieldset>
              <legend className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <Contrast className="h-4 w-4 ml-1" />
                ניגודיות גבוהה
              </legend>
              <button
                onClick={handleContrastToggle}
                className={`flex items-center px-3 py-2 rounded text-sm transition-colors ${
                  highContrast
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
                aria-pressed={highContrast}
              >
                {highContrast ? 'מופעל' : 'כבוי'}
              </button>
            </fieldset>

            {/* Skip Links Section */}
            <fieldset>
              <legend className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <SkipForward className="h-4 w-4 ml-1" />
                קישורי דילוג
              </legend>
              <div className="space-y-2">
                <a 
                  href="#main-content" 
                  className="block w-full text-center px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-sm transition-colors"
                  onClick={(e) => {
                    e.preventDefault();
                    const element = document.getElementById('main-content');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      element.focus();
                    }
                    closeControls();
                  }}
                >
                  דלג לתוכן הראשי
                </a>
                <a 
                  href="#navigation" 
                  className="block w-full text-center px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-sm transition-colors"
                  onClick={(e) => {
                    e.preventDefault();
                    const navigationElement = document.getElementById('navigation');
                    if (navigationElement) {
                      // Page has header - jump to navigation
                      navigationElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      navigationElement.focus();
                    } else {
                      // Page has no header - jump to top of page
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                      document.body.focus();
                    }
                    closeControls();
                  }}
                >
                  דלג לניווט
                </a>
                <a 
                  href="#footer" 
                  className="block w-full text-center px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-sm transition-colors"
                  onClick={(e) => {
                    e.preventDefault();
                    const footerElement = document.getElementById('footer');
                    if (footerElement) {
                      // Page has footer - jump to footer
                      footerElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      footerElement.focus();
                    } else {
                      // Page has no footer - jump to bottom of page
                      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
                      document.body.focus();
                    }
                    closeControls();
                  }}
                >
                  דלג לכותרת תחתונה
                </a>
              </div>
            </fieldset>

            {/* Screen Reader Announcement */}
            <div>
              <button
                onClick={() => announce('הדף נטען בהצלחה', 'assertive')}
                className="flex items-center px-3 py-2 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 transition-colors w-full justify-center"
              >
                <Volume2 className="h-4 w-4 ml-1" />
                בדיקת קורא מסך
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AccessibilityControls;
